import * as types from "./programActionTypes";
import * as exposureActions from "store/actions/input/exposure/exposureActions";

export const updateProgram = (values) => ({
  type: types.UPDATE_PROGRAM,
  payload: values,
});

export const updateInsured = (insuredName) => ({
  type: types.UPDATE_INSURED_NAME,
  payload: insuredName,
});

export const updateRiskDescription = (riskDescription) => ({
  type: types.UPDATE_RISK_DESCRIPTION,
  payload: riskDescription,
});

export const updateInception = (inception) => ({
  type: types.UPDATE_INCEPTION,
  payload: inception,
});

export const updateExpiration = (expiration) => ({
  type: types.UPDATE_EXPIRATION,
  payload: expiration,
});

export const updateProRataAdjustment = (adjustment) => ({
  type: types.UPDATE_PRO_RATA_ADJUSTMENT,
  payload: adjustment,
});

export const updateReference = (reference) => ({
  type: types.UPDATE_REFERENCE,
  payload: reference,
});

export const updateWhetherInUS = (inUS) => ({
  type: types.UPDATE_WHETHER_IN_US,
  payload: inUS,
});

export const updateTurnover = (turnover) => ({
  type: types.UPDATE_TURNOVER,
  payload: turnover,
});

export const updateModel = (model) => ({
  type: types.UPDATE_MODEL,
  payload: model,
});

export const updateBrokerage = (brokerage) => ({
  type: types.UPDATE_BROKERAGE,
  payload: brokerage,
});

export const updateStateOfFiling = (state) => ({
  type: types.UPDATE_STATE_OF_FILING,
  payload: state,
});

export const updateRequestedBrokerage = (brokerage) => ({
  type: types.UPDATE_REQUESTED_BROKERAGE,
  payload: brokerage,
});

export const updateOtherAcquisitionCosts = (costs) => ({
  type: types.UPDATE_OTHER_ACQUISITION_COSTS,
  payload: costs,
});

export const updateProfit = (profit) => ({
  type: types.UPDATE_PROFIT,
  payload: profit,
});

export const updateExpenses = (expenses) => ({
  type: types.UPDATE_EXPENSES,
  payload: expenses,
});

export const updateIndustryClass = (industryCategory) => (
  dispatch,
  getState
) => {
  dispatch({
    type: types.UPDATE_INDUSTRY_CLASS,
    payload: industryCategory,
  });
  dispatch(exposureActions.updateExposureMeasures([industryCategory]));
};

export const updateInsuredAddress = (address) => ({
  type: types.UPDATE_INSURED_ADDRESS,
  payload: address,
});

export const updateMailingAddress = (address) => ({
  type: types.UPDATE_MAILING_ADDRESS,
  payload: address,
});

export const updateInsuredAddressComponents = (components) => ({
  type: types.UPDATE_INSURED_ADDRESS_COMPONENTS,
  payload: components,
});

export const updatePriorSubmission = (priorSubmissionFilename) => ({
  type: types.UPDATE_PRIOR_SUBMISSION,
  payload: priorSubmissionFilename,
});

export const updateCurrency = (currency) => ({
  type: types.UPDATE_CURRENCY,
  payload: currency,
});

export const updateReassured = (reassured) => ({
  type: types.UPDATE_REASSURED,
  payload: reassured,
});

// Partial update of the RRRe cession fields (reinsured, mga, cessionLimitFrom,
// cessionLimitTo); the reducer normalises "" to null.
export const updateCession = (values) => ({
  type: types.UPDATE_CESSION,
  payload: values,
});

export const updatePaymentTerms = (paymentTerms) => ({
  type: types.UPDATE_PAYMENT_TERMS,
  payload: paymentTerms ?? null,
});

export const addDuns = (duns) => ({
  type: types.ADD_DUNS,
  payload: duns,
});

export const removeDuns = (duns) => ({
  type: types.REMOVE_DUNS,
  payload: duns,
});

export const clearCompanyNaics = () => ({
  type: types.CLEAR_COMPANY_NAICS,
});

export const setCompanyNaics = (companyNaics) => ({
  type: types.SET_COMPANY_NAICS,
  payload: companyNaics,
});

// NAICS derived from the GL Pricing classifications. The reducer keeps a
// manually chosen NAICS, so this never overwrites an underwriter's pick.
export const setDerivedNaics = (naics) => ({
  type: types.SET_DERIVED_NAICS,
  payload: naics,
});

export const updateNotes = (notes) => ({
  type: types.UPDATE_PROGRAM_NOTES,
  payload: notes,
});

export const updateRiskSummary = (summary) => ({
  type: types.UPDATE_RISK_SUMMARY,
  payload: summary,
});

export const updateDataQuality = (quality) => ({
  type: types.UPDATE_DATA_QUALITY,
  payload: quality,
});

export const updateSurplusLinesTaxReporting = (field, value) => ({
  type: types.UPDATE_SURPLUS_LINES_TAX_REPORTING,
  payload: { field, value },
});

export const seedSurplusLinesTaxReporting = (values) => ({
  type: types.SEED_SURPLUS_LINES_TAX_REPORTING,
  payload: values,
});

export const updateAutoExposureRating = (field, value) => ({
  type: types.UPDATE_AUTO_EXPOSURE_RATING,
  payload: { field, value },
});

export const updateLossRating = (field, value) => ({
  type: types.UPDATE_LOSS_RATING,
  payload: { field, value },
});

export const updateLossRatingYear = (yearIndex, field, value) => ({
  type: types.UPDATE_LOSS_RATING_YEAR,
  payload: { yearIndex, field, value },
});

export const updateAutoLossRating = (field, value) => ({
  type: types.UPDATE_AUTO_LOSS_RATING,
  payload: { field, value },
});

export const updateExperienceRating = (field, value) => ({
  type: types.UPDATE_EXPERIENCE_RATING,
  payload: { field, value },
});

export const updateXsRating = (field, value) => ({
  type: types.UPDATE_XS_RATING,
  payload: { field, value },
});

// `section` addresses one of the four year blocks (primaryCurrent, primaryExpiring,
// excessCurrent, excessExpiring); omit it for the top-level keys.
export const updateRenewalRateChange = (section, field, value) => ({
  type: types.UPDATE_RENEWAL_RATE_CHANGE,
  payload: { section, field, value },
});

export const setAutoPricingResult = (result) => ({
  type: types.SET_AUTO_PRICING_RESULT,
  payload: result,
});

export const setLossRatingResult = (result) => ({
  type: types.SET_LOSS_RATING_RESULT,
  payload: result,
});

export const setAutoLossRatingResult = (result) => ({
  type: types.SET_AUTO_LOSS_RATING_RESULT,
  payload: result,
});

export const setExperienceRatingResult = (result) => ({
  type: types.SET_EXPERIENCE_RATING_RESULT,
  payload: result,
});

export const setXsRatingResult = (result) => ({
  type: types.SET_XS_RATING_RESULT,
  payload: result,
});

export const setXsRatingAutoCalculate = (value) => ({
  type: types.SET_XS_RATING_AUTO_CALCULATE,
  payload: value,
});
